import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  getAllDocsSnap,
  setDocData,
} from "../../../utils/firebase/Firebase.utils";
import IOSSpinner from "../../../components/IOSSpinner";
import BentoTable from "../../../components/BentoTable";
import { slicedData } from "../../../utils/EleghoUtils";
import { useCompany } from "../../../context/CompanyContext";
import CompanyTerms from "../../../components/CompanyTerms";
import PageModal from "../Components/PageModal";
import {
  FiTool,
  FiClock,
  FiCheckCircle,
  FiAlertTriangle,
  FiCalendar,
  FiPackage,
  FiSearch,
  FiX,
} from "react-icons/fi";
import toast from "react-hot-toast";
import ServiceRequestDetails from "./ServiceRequestDetails";

function ServiceRequests() {
  const currentUser = useSelector((state) => state.userObject);
  const [serviceRequests, setServiceRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  const [numberOfItemPerPage, setNumberOfItemPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [viewMode, setViewMode] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const { terms } = useCompany();
  const [editMode, setEditMode] = useState(false);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [savingComment, setSavingComment] = useState(false);
  const [statusUpdateLoading, setStatusUpdateLoading] = useState(false);
  const [assignedToArray, setAssignedToArray] = useState([]);
  const [resolutionText, setResolutionText] = useState("");
  const [resolutionSaving, setResolutionSaving] = useState(false);
  const [assignmentSaving, setAssignmentSaving] = useState(false);
  const [newStatus, setNewStatus] = useState("");
  const [updateHappened, setUpdateHappened] = useState(false);

  useEffect(() => {
    const fetchServiceRequests = async () => {
      await getAllDocsSnap("serviceRequests", (docs) => {
        // Sort by createdAt
        const sortedDocs = docs.sort((a, b) => {
          return new Date(b.createdAt) - new Date(a.createdAt);
        });
        setServiceRequests(sortedDocs);
        setFilteredRequests(sortedDocs);
        setLoading(false);
      });
    };

    fetchServiceRequests();
  }, []);

  useEffect(() => {
    // Apply filters
    let filtered = [...serviceRequests];

    // Apply status filter
    if (activeFilter !== "All") {
      filtered = filtered.filter((request) => request.status === activeFilter);
    }

    // Apply search filter
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      filtered = filtered.filter((request) => {
        // Search in service type
        if (request.serviceType?.toLowerCase().includes(term)) return true;
        // Search in description
        if (request.description?.toLowerCase().includes(term)) return true;
        // Search in unit identifier
        if (request.identifier?.toLowerCase().includes(term)) return true;
        // Search in user name
        if (request.userName?.toLowerCase().includes(term)) return true;
        return false;
      });
    }

    setFilteredRequests(filtered);
  }, [serviceRequests, activeFilter, searchTerm]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const clearSearch = () => {
    setSearchTerm("");
  };

  const handleViewMode = (request) => {
    setViewMode(!viewMode);
    setSelectedRequest(request);
    if (request) {
      setComments(request.comments || []);
      setAssignedToArray(request.assignedTo || []);
      setResolutionText(request.resolution || "");
      setNewStatus(request.status);
      setEditMode(false);
      setUpdateHappened(false);
    }
  };

  const dataSliced = slicedData(
    filteredRequests,
    numberOfItemPerPage,
    currentPage
  );

  const updateRequestStatus = async (id, newStatus) => {
    setIsUpdating(true);
    try {
      await setDocData("serviceRequests", id, { status: newStatus });
      toast.success(`Request status updated to ${newStatus}`);
      setViewMode(false);
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Failed to update request status");
    } finally {
      setIsUpdating(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString();
  };

  const filters = [
    "All",
    "Pending",
    "Scheduled",
    "In Progress",
    "Completed",
    "Cancelled",
  ];

  // Calculate stats
  const stats = {
    totalRequests: serviceRequests.length,
    pendingRequests: serviceRequests.filter((r) => r.status === "Pending")
      .length,
    inProgressRequests: serviceRequests.filter(
      (r) => r.status === "In Progress"
    ).length,
    completedRequests: serviceRequests.filter((r) => r.status === "Completed")
      .length,
  };

  const getStatusBadge = (status) => {
    const baseClasses = "px-2.5 py-0.5 rounded-full text-xs font-medium";

    switch (status) {
      case "Pending":
        return (
          <span className={`${baseClasses} bg-yellow-100 text-yellow-800`}>
            <div className="flex items-center gap-1">
              <FiClock className="w-3 h-3" />
              <span>Pending</span>
            </div>
          </span>
        );
      case "Scheduled":
        return (
          <span className={`${baseClasses} bg-blue-100 text-blue-800`}>
            <div className="flex items-center gap-1">
              <FiCalendar className="w-3 h-3" />
              <span>Scheduled</span>
            </div>
          </span>
        );
      case "In Progress":
        return (
          <span className={`${baseClasses} bg-purple-100 text-purple-800`}>
            <div className="flex items-center gap-1">
              <FiTool className="w-3 h-3" />
              <span>In Progress</span>
            </div>
          </span>
        );
      case "Completed":
        return (
          <span className={`${baseClasses} bg-green-100 text-green-800`}>
            <div className="flex items-center gap-1">
              <FiCheckCircle className="w-3 h-3" />
              <span>Completed</span>
            </div>
          </span>
        );
      case "Cancelled":
        return (
          <span className={`${baseClasses} bg-red-100 text-red-800`}>
            <div className="flex items-center gap-1">
              <FiX className="w-3 h-3" />
              <span>Cancelled</span>
            </div>
          </span>
        );
      default:
        return (
          <span className={`${baseClasses} bg-gray-100 text-gray-800`}>
            {status}
          </span>
        );
    }
  };

  const handleSelectStatus = (selected) => {
    setNewStatus(selected.value);
    setUpdateHappened(true);
  };

  const handleUpdateStatus = async () => {
    setStatusUpdateLoading(true);
    try {
      if (newStatus === selectedRequest.status) {
        setStatusUpdateLoading(false);
        return;
      }

      if (newStatus === "Completed" && !selectedRequest.resolution) {
        toast.error("Please add a resolution before marking as completed");
        setStatusUpdateLoading(false);
        return;
      }

      const updatedRequest = {
        ...selectedRequest,
        status: newStatus,
        updatedAt: new Date().toISOString(),
        updatedBy: currentUser.uid,
        updatedName: currentUser.displayName,
        history: [
          ...(selectedRequest?.history || []),
          {
            action: "Status changed to",
            at: new Date().toLocaleString(),
            by: currentUser.displayName,
            to: newStatus,
          },
        ],
      };

      await setDocData("serviceRequests", selectedRequest.id, updatedRequest);
      setSelectedRequest(updatedRequest);
      toast.success(`Request status updated to ${newStatus}`);
      setUpdateHappened(false);
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Failed to update request status");
    } finally {
      setStatusUpdateLoading(false);
    }
  };

  const handleCommentSubmit = async (commentText) => {
    setSavingComment(true);
    try {
      const commentObject = {
        message: commentText,
        createdBy: currentUser.displayName,
        createdAt: new Date().toLocaleString(),
        email: currentUser.email,
        id: Date.now().toString(),
      };

      const updatedComments = [
        ...(selectedRequest.comments || []),
        commentObject,
      ];

      const updatedRequest = {
        ...selectedRequest,
        comments: updatedComments,
        updatedAt: new Date().toISOString(),
        history: [
          ...(selectedRequest?.history || []),
          {
            action: "Comment added",
            at: new Date().toLocaleString(),
            by: currentUser.displayName,
            to:
              commentText.substring(0, 30) +
              (commentText.length > 30 ? "..." : ""),
          },
        ],
      };

      await setDocData("serviceRequests", selectedRequest.id, updatedRequest);
      setSelectedRequest(updatedRequest);
      setComments(updatedComments);
      toast.success("Comment added successfully");
      return true;
    } catch (error) {
      console.error("Error adding comment:", error);
      toast.error("Failed to add comment");
      return false;
    } finally {
      setSavingComment(false);
    }
  };

  const handleCommentDelete = async (commentId) => {
    if (!window.confirm("Are you sure you want to delete this comment?"))
      return;

    try {
      const updatedComments = (selectedRequest.comments || []).filter(
        (comment) => comment.id !== commentId
      );

      const updatedRequest = {
        ...selectedRequest,
        comments: updatedComments,
        updatedAt: new Date().toISOString(),
        history: [
          ...(selectedRequest?.history || []),
          {
            action: "Comment deleted",
            at: new Date().toLocaleString(),
            by: currentUser.displayName,
            to: "",
          },
        ],
      };

      await setDocData("serviceRequests", selectedRequest.id, updatedRequest);
      setSelectedRequest(updatedRequest);
      setComments(updatedComments);
      toast.success("Comment deleted successfully");
    } catch (error) {
      console.error("Error deleting comment:", error);
      toast.error("Failed to delete comment");
    }
  };

  const handleResolutionChange = (e) => {
    setResolutionText(e.target.value);
  };

  const handleResolutionUpdate = async () => {
    setResolutionSaving(true);
    try {
      const updatedRequest = {
        ...selectedRequest,
        resolution: resolutionText,
        updatedAt: new Date().toISOString(),
        updatedBy: currentUser.uid,
        updatedName: currentUser.displayName,
        history: [
          ...(selectedRequest?.history || []),
          {
            action: "Resolution updated",
            at: new Date().toLocaleString(),
            by: currentUser.displayName,
            to:
              resolutionText.substring(0, 30) +
              (resolutionText.length > 30 ? "..." : ""),
          },
        ],
      };

      await setDocData("serviceRequests", selectedRequest.id, updatedRequest);
      setSelectedRequest(updatedRequest);
      toast.success("Resolution updated successfully");
    } catch (error) {
      console.error("Error updating resolution:", error);
      toast.error("Failed to update resolution");
    } finally {
      setResolutionSaving(false);
    }
  };

  const handleAssignmentUpdate = async () => {
    setAssignmentSaving(true);
    try {
      const assigneeNames = assignedToArray.map((a) => a.label).join(", ");

      const updatedRequest = {
        ...selectedRequest,
        assignedTo: assignedToArray,
        updatedAt: new Date().toISOString(),
        updatedBy: currentUser.uid,
        updatedName: currentUser.displayName,
        history: [
          ...(selectedRequest?.history || []),
          {
            action: "Service request assigned to",
            at: new Date().toLocaleString(),
            by: currentUser.displayName,
            to: assigneeNames || "No one",
          },
        ],
      };

      await setDocData("serviceRequests", selectedRequest.id, updatedRequest);
      setSelectedRequest(updatedRequest);
      toast.success("Assignment updated successfully");
    } catch (error) {
      console.error("Error updating assignment:", error);
      toast.error("Failed to update assignment");
    } finally {
      setAssignmentSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center">
        <IOSSpinner />
      </div>
    );
  }

  return (
    <div className="text-xs">
      <div className="text-sm font-bold text-[#363062] uppercase text-left">
        Service Requests Management
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 my-6">
        <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gray-50 rounded-lg">
              <FiPackage className="w-5 h-5 text-gray-600" />
            </div>
            <div>
              <div className="text-sm text-gray-600">Total Requests</div>
              <div className="text-2xl font-semibold text-gray-900">
                {stats.totalRequests}
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-50 rounded-lg">
              <FiClock className="w-5 h-5 text-yellow-500" />
            </div>
            <div>
              <div className="text-sm text-gray-600">Pending</div>
              <div className="text-2xl font-semibold text-yellow-500">
                {stats.pendingRequests}
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-50 rounded-lg">
              <FiTool className="w-5 h-5 text-purple-500" />
            </div>
            <div>
              <div className="text-sm text-gray-600">In Progress</div>
              <div className="text-2xl font-semibold text-purple-500">
                {stats.inProgressRequests}
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-50 rounded-lg">
              <FiCheckCircle className="w-5 h-5 text-green-500" />
            </div>
            <div>
              <div className="text-sm text-gray-600">Completed</div>
              <div className="text-2xl font-semibold text-green-500">
                {stats.completedRequests}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="flex flex-col md:flex-row items-center justify-between mb-4 gap-4">
        <div className="flex items-center justify-start gap-2 flex-wrap w-full md:w-auto">
          {filters.map((filter, index) => (
            <div
              key={index}
              className={`cursor-pointer rounded-full px-4 py-1 text-sm border border-gray-300 ${
                activeFilter === filter
                  ? "bg-[#363062] text-white border-[#363062]"
                  : "bg-white text-gray-700 hover:bg-gray-50"
              }`}
              onClick={() => setActiveFilter(filter)}
            >
              {filter}
            </div>
          ))}
        </div>
        <div className="relative w-full md:w-60">
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
            <FiSearch className="w-4 h-4 text-gray-500" />
          </div>
          <input
            type="search"
            name="search"
            className="block w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg text-sm"
            placeholder={`Search by ${
              terms.identifier || "unit"
            }, type, or name`}
            value={searchTerm}
            onChange={handleSearch}
          />
          {searchTerm && (
            <button
              className="absolute inset-y-0 right-0 flex items-center pr-3"
              onClick={clearSearch}
            >
              <FiX className="w-4 h-4 text-gray-500" />
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <BentoTable
        label="Service Requests"
        data={filteredRequests}
        numberOfItemPerPage={numberOfItemPerPage}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
      >
        <div className="divide-y divide-gray-100">
          {/* Table Header */}
          <div className="grid grid-cols-1 md:grid-cols-7 gap-2 font-semibold py-3 px-4 bg-gray-50 text-gray-700 text-xs rounded-t-lg">
            <div className="font-medium text-[#363062]">
              <CompanyTerms term="identifier" capitalize={true} />
            </div>
            <div className="capitalize text-gray-700">Service Type</div>
            <div className="text-gray-700">Requested By</div>
            <div className="text-gray-600">Date Created</div>
            <div className="text-gray-700">Preferred Date</div>
            <div className="text-gray-700">Preferred Time</div>
            <div className="flex justify-center">Status</div>
          </div>

          {/* Table Rows */}
          {dataSliced.length === 0 ? (
            <div className="py-10 text-center text-gray-500">
              No service requests found
            </div>
          ) : (
            dataSliced.map((request, index) => (
              <div
                key={index}
                className="grid grid-cols-1 md:grid-cols-7 gap-2 p-4 hover:bg-gray-50 cursor-pointer"
                onClick={() => handleViewMode(request)}
              >
                <div className="font-medium text-[#363062]">
                  {request.identifier}
                </div>
                <div className="text-gray-700">{request.serviceType}</div>
                <div className="text-gray-700">{request.userName || "N/A"}</div>
                <div className="text-gray-600">
                  {formatDate(request.createdAt)}
                </div>
                <div className="text-gray-700">
                  {formatDate(request.preferredDate)}
                </div>
                <div className="text-gray-700">
                  {request.preferredTime || "N/A"}
                </div>
                <div className="flex justify-center">
                  {getStatusBadge(request.status)}
                </div>
              </div>
            ))
          )}
        </div>
      </BentoTable>

      {/* Details Modal */}
      {viewMode && (
        <PageModal>
          <ServiceRequestDetails
            request={selectedRequest}
            onClose={() => setViewMode(false)}
            setSelectedRequest={setSelectedRequest}
          />
        </PageModal>
      )}
    </div>
  );
}

export default ServiceRequests;
